import React from 'react';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import cn from 'classnames';

import Box from '@/design_system/Box';
import InlineText from '@/design_system/Typography/InlineText';
import IconCancel from '@/icons/Cancel.svg';
import IconDone from '@/icons/Done.svg';
import IconZendesk from '@/icons/Zendesk.svg';
import {
  ARTICLE_ARCHIVAL_TYPES,
  ARTICLE_CANCELLATION_TYPES,
  ARTICLE_DISPLAYED_STEPS,
  getDisplayedStep,
} from '@/models/article';
import { RequestWithRelationsFromRequestsList } from '@/models/request';
import useViewPort from '@/utils/useViewport';

import './RequestDisplayedSteps.css';

type Step = {
  label: string;
  icon?: React.ReactNode;
  variant?: 'success' | 'warning' | 'danger';
};

const useRequestDisplayedSteps = (request: RequestWithRelationsFromRequestsList): Step[] => {
  const { _ } = useLingui();

  const steps = request.allArticles
    .map((article) => {
      const { archivalDetail, cancellationDetail, hasIssue } = article;
      const archivalType = ARTICLE_ARCHIVAL_TYPES.find(
        (articleArchivalType) => articleArchivalType.id === archivalDetail?.type
      );
      const cancellationType = ARTICLE_CANCELLATION_TYPES.find(
        (articleCancellationType) => articleCancellationType.id === cancellationDetail?.type
      );

      const isArticleRecycled = !!article.archivedAt && archivalType?.id === 'in-recycling';
      const isArticleCompleted = !!article.archivedAt && archivalType?.id === 'completed';
      const isArticleCancelled = !!article.archivedAt && !!cancellationType;
      const isArticleExportedToZendesk = article.archivalDetail?.exportedTo === 'zendesk';
      const isArticleArchivedOrCompletedWithArchivalType = !!article.archivedAt && archivalType;
      const displayedStep = getDisplayedStep(article.step);
      const articleDisplayedStep = ARTICLE_DISPLAYED_STEPS.find(({ id }) => displayedStep === id);

      if (isArticleCancelled) {
        const label = articleDisplayedStep
          ? `${_(cancellationType.name)}: ${_(articleDisplayedStep.name)}`
          : _(cancellationType.name);

        return {
          label,
          icon: <IconCancel />,
          variant: 'danger',
        };
      }

      if (isArticleCompleted) {
        // Avoid to display something like "Completed: Completed"
        const label =
          articleDisplayedStep && _(archivalType.name) !== _(articleDisplayedStep.name)
            ? `${_(archivalType.name)}: ${_(articleDisplayedStep.name)}`
            : _(archivalType.name);

        return {
          label,
          icon: <IconDone />,
          variant: 'success',
        };
      }

      if (isArticleExportedToZendesk) {
        return {
          label: _(msg({ id: 'article.archival-type.zendesk', message: 'Transferred to Zendesk' })),
          icon: <IconZendesk />,
        };
      }

      if (isArticleRecycled) {
        return {
          label: _(msg({ id: 'article.archival-type.in-recycling', message: 'In recycling' })),
          icon: <IconDone />,
          variant: 'success',
        };
      }

      if (isArticleArchivedOrCompletedWithArchivalType) {
        const label =
          articleDisplayedStep && _(archivalType.name) !== _(articleDisplayedStep.name)
            ? `${_(archivalType.name)}: ${_(articleDisplayedStep.name)}`
            : _(archivalType.name);

        return {
          label,
          icon: <IconCancel />,
          variant: 'danger',
        };
      }

      if (hasIssue) {
        const troubleshootingLabel = msg({
          id: 'article.issue.troubleshooting',
          message: 'Troubleshooting',
        });

        const label = articleDisplayedStep
          ? `${_(troubleshootingLabel)}: ${_(articleDisplayedStep.name)}`
          : _(troubleshootingLabel);

        return {
          label,
          variant: 'danger',
        };
      }

      if (articleDisplayedStep) {
        return { label: _(articleDisplayedStep.name) };
      }
    })
    .filter((step) => !!step) as Step[];

  return groupStepsByQuantity(steps);
};

/**
 *
 * Formats a list of steps so that steps of the same label are merged into a single step, with the number of duplicates in the label.
 * Example: [{ label: 'Archived: Diagnosis (x2)' }]
 */

const groupStepsByQuantity = (steps: Step[]): Step[] => {
  const stepsWithQuantity = steps.reduce<(Step & { quantity: number })[]>((acc, step) => {
    const existingStep = acc.find((accStep) => areStepsDuplicates(step, accStep));

    if (!existingStep) {
      acc.push(
        Object.assign(step, {
          quantity: 1,
        })
      );
    } else {
      existingStep.quantity = existingStep.quantity + 1;
    }

    return acc;
  }, []);

  return formatStepLabelsWithQuantity(stepsWithQuantity);
};

/**
 *
 * @returns Returns true if both steps have the same label
 */
const areStepsDuplicates = (step1: Step, step2: Step): boolean => {
  return step1.label === step2.label;
};

/**
 *
 * @returns Steps with their label formatted if they have duplicates. Example: [{ label: 'Archived: Diagnosis (x2)' }]
 */
const formatStepLabelsWithQuantity = (
  stepsWithQuantity: (Step & { quantity: number })[]
): Step[] => {
  return stepsWithQuantity.map((stepWithQuantity) => {
    const newLabel =
      stepWithQuantity.quantity > 1
        ? `${stepWithQuantity.label} (x${stepWithQuantity.quantity})`
        : stepWithQuantity.label;

    return { ...stepWithQuantity, label: newLabel };
  });
};

export const RequestDisplayedSteps = ({
  request,
}: {
  request: RequestWithRelationsFromRequestsList;
}) => {
  const displayedSteps = useRequestDisplayedSteps(request);
  const { isMobile } = useViewPort();

  if (isMobile) {
    return (
      <>
        {displayedSteps.map((step, index) => {
          return (
            <Box padding="0.25rem 0.5rem" style={{ flex: 'none' }} key={index}>
              {step.icon ? (
                <div
                  className={cn('request-displayed-step', {
                    'has-icon': !!step.icon,
                  })}
                >
                  {step.icon}
                  <InlineText size="200" color={step.variant}>
                    {step.label}
                  </InlineText>
                </div>
              ) : (
                <InlineText size="200" color={step.variant}>
                  {step.label}
                </InlineText>
              )}
            </Box>
          );
        })}
      </>
    );
  }

  // Display step icons if all steps have an icon otherwise display bullets to avoid icon misalignment
  const displayStepIcons = displayedSteps.every((step) => !!step.icon);

  return (
    <ul
      style={
        displayedSteps.length <= 1 || displayStepIcons
          ? { listStyle: 'none', padding: 0 }
          : undefined
      }
    >
      {!displayedSteps.length && <li>-</li>}
      {!!displayedSteps.length &&
        displayedSteps.map((step, index) => (
          <li
            key={index}
            className={cn('request-displayed-step', {
              'has-icon': displayStepIcons && !!step.icon,
              'text-success': step.variant === 'success',
              'text-warning': step.variant === 'warning',
              'text-danger': step.variant === 'danger',
            })}
          >
            {displayStepIcons && step.icon}
            {step.label}
          </li>
        ))}
    </ul>
  );
};
